import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2, CheckCircle2, XCircle, Clock, CalendarDays, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

const MAX_POLLS = 12;
const POLL_INTERVAL = 1500;
const SLOW_INTERVAL = 5000;
const SLOW_MAX = 24; // ~2 more minutes of quiet background recovery after the fast burst

// Top-level /bloqs route. Stripe ticket checkout redirects here as {origin}/bloqs?ticket_session=…&event_id=…
// If there are no ticket params, this is just a bare /bloqs hit → send the user into the app.
export default function TicketReturn() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState("polling");
  const [checking, setChecking] = useState(false);
  const pollCount = useRef(0);
  const stoppedRef = useRef(false);
  const timerRef = useRef(null);
  const hasToken = typeof window !== "undefined" && !!localStorage.getItem("bloq_token");

  const session = searchParams.get("ticket_session");
  const eventId = searchParams.get("event_id");
  const canceled = searchParams.get("ticket_canceled");
  const isReturn = !!session || !!canceled;

  const { data: eventData } = useQuery({
    queryKey: ["event", eventId],
    queryFn: () => api.getEvent(eventId),
    enabled: hasToken && !!eventId && state === "success",
    retry: false,
  });

  const settleSuccess = () => {
    stoppedRef.current = true;
    if (timerRef.current) clearTimeout(timerRef.current);
    setState("success");
  };

  useEffect(() => {
    if (!isReturn || !hasToken) return;
    if (canceled) { setState("canceled"); return; }
    if (!session || !eventId) { setState("error"); return; }
    stoppedRef.current = false;
    pollCount.current = 0;
    let slow = 0;
    const poll = async () => {
      if (stoppedRef.current) return;
      pollCount.current += 1;
      try {
        const res = await api.eventTicketStatus(eventId, session);
        if (res.granted || res.payment_status === "paid") { settleSuccess(); return; }
      } catch {
        /* keep trying */
      }
      if (stoppedRef.current) return;
      if (pollCount.current < MAX_POLLS) {
        timerRef.current = setTimeout(poll, POLL_INTERVAL);
      } else {
        setState("pending");
        if (slow < SLOW_MAX) { slow += 1; timerRef.current = setTimeout(poll, SLOW_INTERVAL); }
      }
    };
    poll();
    return () => { stoppedRef.current = true; if (timerRef.current) clearTimeout(timerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAgain = async () => {
    if (checking || stoppedRef.current || !session || !eventId) return;
    setChecking(true);
    try {
      const res = await api.eventTicketStatus(eventId, session);
      if (res.granted || res.payment_status === "paid") settleSuccess();
    } catch {
      /* stay pending — background recovery continues */
    } finally {
      setChecking(false);
    }
  };

  if (!isReturn) return <Navigate to="/app/bloqs" replace />;
  if (!hasToken) return <Navigate to="/login" replace />;

  const bloqId = eventData?.event?.circle_id;
  const goToBloq = () => navigate(bloqId ? `/app/bloqs/${bloqId}?info=1` : "/app/bloqs");

  const map = {
    polling: { icon: Loader2, cls: "text-bloq-muted", title: "Confirming your ticket…", sub: "This only takes a moment.", spin: true },
    success: { icon: CheckCircle2, cls: "text-bloq-mint", title: "You're going! 🎟️", sub: "Your ticket is confirmed. See you there." },
    pending: { icon: Clock, cls: "text-amber-300", title: "Payment processing", sub: "Your ticket will appear in the bloq once payment settles — we'll keep checking automatically." },
    canceled: { icon: XCircle, cls: "text-bloq-muted", title: "Checkout canceled", sub: "No charge was made. You can grab a ticket anytime." },
    error: { icon: XCircle, cls: "text-destructive", title: "Something went wrong", sub: "We couldn't confirm your ticket. Please try again from the bloq." },
  };
  const c = map[state] || map.error;
  const Icon = c.icon;

  return (
    <div className="flex min-h-screen items-center justify-center bg-bloq-bg px-6" data-testid="ticket-return-page">
      <div className="w-full max-w-sm rounded-3xl border border-white/10 bg-bloq-s1 p-8 text-center">
        <div className={cn("mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-white/5", c.cls)}>
          <Icon className={cn("h-7 w-7", c.spin && "animate-spin")} />
        </div>
        <h1 className="mt-4 text-xl font-bold text-white" data-testid="ticket-return-title">{c.title}</h1>
        <p className="mt-1 text-sm text-bloq-muted">{c.sub}</p>
        {eventData?.event?.title && state === "success" && (
          <div className="mt-3 flex items-center justify-center gap-1.5 text-sm text-white/85">
            <CalendarDays className="h-4 w-4 text-bloq-purple" /> {eventData.event.title}
          </div>
        )}
        {state === "pending" && (
          <button onClick={checkAgain} disabled={checking} className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-bloq-mint underline underline-offset-2 hover:opacity-80 disabled:opacity-50" data-testid="ticket-return-check-again">
            {checking && <Loader2 className="h-4 w-4 animate-spin" />} Check again
          </button>
        )}
        <Button onClick={goToBloq} disabled={state === "polling"} className="mt-6 w-full rounded-full bloq-gradient font-bold text-bloq-bg hover:opacity-90 disabled:opacity-50" data-testid="ticket-return-continue">
          Go to bloq <ArrowRight className="ml-1 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
